import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { CHIVO, Page, Steps, useMobile, useSite } from "@/components/site/Chrome";

export default function FreeExam() {
  const mob = useMobile();
  const site = useSite();
  const [params] = useSearchParams();
  const certs = site?.certs || [];
  const [slug, setSlug] = useState(params.get("cert") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [sent, setSent] = useState(params.get("sent") === "1" ? { email: params.get("email"), dev: params.get("dev") } : null);
  const [busy, setBusy] = useState(false);
  const chosen = certs.find((c) => c.cert_slug === slug) || certs[0];
  const send = async (e) => {
    e?.preventDefault();
    const target = slug || chosen?.cert_slug;
    if (!target) return;
    setBusy(true);
    try {
      const { data } = await api.post("/free/start", { cert_slug: target, email: email.trim().toLowerCase() });
      setSent({ email: email.trim().toLowerCase(), dev: data.dev_magic_link });
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const pad = mob ? 20 : 32;
  return (
    <Page title="Free sample exam — ExamRay" description="A real timed sample exam with the same domain report as the paid set. Magic link, no password, no card." testid="free-exam-page">
      <section style={{ padding: `64px ${pad}px`, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 48, alignItems: "start" }}>
        <div>
          <p className="kicker deep">FREE <span className="dash">{chosen?.free_count ?? "—"}</span>-QUESTION EXAM</p>
          <h1 style={{ marginTop: 14, fontFamily: CHIVO, fontWeight: 900, fontSize: 42, lineHeight: 1.07, letterSpacing: "-0.03em" }}>Three steps, no password.</h1>
          <div style={{ marginTop: 30 }}>
            <Steps testid="free-steps" items={[
              { title: "Enter your email", body: "Used to hold your score report and to send the link. Nothing else." },
              { title: "Open the magic link", body: "Valid for 24 hours, one use. It signs you straight into the exam." },
              { title: `Take ${chosen?.free_count ?? ""} questions, keep the report`, body: `Timed at ${chosen?.free_minutes ?? ""} minutes with domain scoring and full explanations.` }]} />
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {!sent && (
            <form onSubmit={send} style={{ border: "1px solid var(--ac)" }} data-testid="free-form">
              <div className="er-rule" />
              <div style={{ padding: "28px 26px" }}>
                <p className="label" style={{ color: "var(--ink-4)" }}>STEP 1 OF 3</p>
                <p style={{ marginTop: 14, fontFamily: CHIVO, fontWeight: 700, fontSize: 22 }}>Where should we send it?</p>
                <label className="field-label" style={{ marginTop: 20 }}>EMAIL ADDRESS</label>
                <input data-testid="free-page-email" className="field" type="email" required placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                <label className="field-label" style={{ marginTop: 14 }}>CERTIFICATION</label>
                <select data-testid="free-page-cert" className="field" value={slug || chosen?.cert_slug || ""} onChange={(e) => setSlug(e.target.value)}>
                  {certs.map((c) => <option key={c.cert_slug} value={c.cert_slug}>{c.cert_name}{c.exam_code ? ` (${c.exam_code})` : ""}</option>)}
                </select>
                <button data-testid="free-page-submit" disabled={busy || !certs.length} className="btn primary block" style={{ marginTop: 18 }}>{busy ? "Sending…" : "Send my free exam"}</button>
                <p style={{ marginTop: 12, fontSize: 12.5, color: "var(--ink-5)", lineHeight: 1.5 }}>By continuing you agree to the Terms of Service. We do not sell or share your email.</p>
              </div>
            </form>
          )}
          {sent && (
            <div style={{ border: "1px solid var(--hair)", background: "var(--surface)", padding: "22px 24px" }} data-testid="free-confirmation">
              <p className="label">CHECK YOUR INBOX</p>
              <p style={{ marginTop: 12, fontWeight: 600, fontSize: 15.5 }}>Check your inbox.</p>
              <p style={{ marginTop: 6, fontSize: 14, lineHeight: 1.6, color: "var(--ink-4)" }}>We sent a link to <span className="mono" style={{ fontSize: 13.5 }}>{sent.email}</span>. It expires in 24 hours. Nothing arrived? Check spam, then resend.</p>
              {sent.dev && <p style={{ marginTop: 10, fontSize: 13.5 }}><a href={sent.dev} data-testid="free-dev-link">Open the link now (dev mail mode)</a></p>}
              <button data-testid="free-resend" onClick={send} disabled={busy} className="btn secondary" style={{ marginTop: 16, padding: "10px 20px", fontSize: 14 }}>Resend link</button>
            </div>
          )}
        </div>
      </section>
    </Page>
  );
}
